import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Button,
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import { getLuggageRackFlowHistory } from '../../../store/page/furnishing/furnishingThunk';

const LuggageRackFlowHistoryModal = ({ isOpen, toggle, stageId, shellNo }) => {
  const dispatch = useDispatch();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [showHistoryDetails, setShowHistoryDetails] = useState(false);
  const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';

    let date;

    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === 'string' && dateInput.includes('T')) {
      date = new Date(dateInput);
    } else if (typeof dateInput === 'string' && dateInput.includes('-')) {
      const parts = dateInput.split(' ');
      if (parts.length === 2) {
        const [datePart, timePart] = parts;
        const [day, month, year] = datePart.split('-');
        const formatted = `${year}-${month}-${day}T${timePart}`;
        date = new Date(formatted);
      }
    }

    if (!date || Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await dispatch(getLuggageRackFlowHistory(stageId)).unwrap();
      console.log('Fetched history:', response);
      setHistory(response?.data || []);
      setApplicationDetails(response.data?.application);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && stageId) {
      fetchHistory();
      setSelectedHistory(null);
      setShowHistoryDetails(false);
    }
  }, [isOpen, stageId]);
console.log('selectedHistory', selectedHistory);

  const getStatusLabel = (status) => {
    const statusMap = {
      0: 'Reinspection',
      1: 'Forwarded',
      2: 'Freeze',
    };
    return statusMap[Number(status)] || 'Unknown';
  };

  const getBadgeClass = (status) => {
    const classMap = {
      0: 'bg-warning text-dark',
      1: 'bg-primary',
      2: 'bg-success',
    };
    return classMap[Number(status)] || 'bg-secondary';
  };

  const handleViewHistoryDetails = (historyItem) => {
    console.log('Viewing history details for:', historyItem);
    setSelectedHistory(historyItem);
    setShowHistoryDetails(true);
  };
  
  const renderRectificationBadge = (status) => {
    if (!status || status === '-') return '-';
    let badgeClass = 'bg-warning text-dark';
    if (status === 'not_rectifiable' || status === 'not_rectified') {
      badgeClass = 'bg-danger';
    }
    if (status === 'rectified' || status === 'rectification_required') {
      badgeClass = 'bg-warning text-dark';
    }
    return <span className={`badge ${badgeClass}`}>{status}</span>;
  };

  const hasValidVisualData = (observations) => {
    if (!Array.isArray(observations) || observations.length === 0) return false;
    return observations.some((item) => {
      return item?.description && item.description !== '-' && item.description.trim() !== '';
    });
  };

  const renderVisualObservationTable = (observations, changes, title) => {
    if (!hasValidVisualData(observations)) return null;

    const getVisualObsChange = (index) => {
      if (!changes || !changes.old || !changes.new) return {};

      const oldArray = changes.old?.visual_obs_one || [];
      const newArray = changes.new?.visual_obs_one || [];

      if (oldArray[index] || newArray[index]) {
        const oldItem = oldArray[index];
        const newItem = newArray[index];

        let oldStatusKey = '-';
        let newStatusKey = '-';
        let oldRectification = '';
        let newRectification = '';

        if (oldItem) {
          oldStatusKey = oldItem?.status_key || oldItem?.status || '-';
          oldRectification = oldItem?.rectification_status || '';
        }

        if (newItem) {
          newStatusKey = newItem?.status_key || newItem?.status || '-';
          newRectification = newItem?.rectification_status || '';
        }

        if (oldStatusKey !== newStatusKey || oldRectification !== newRectification) {
          return {
            old: oldStatusKey,
            new: newStatusKey,
            oldRectification,
            newRectification
          };
        }
      }

      return {};
    };

    return (
      <div className="mb-5">
        <h5 className="mb-3">{title}</h5>
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th width="60">#</th>
                <th>Description</th>
                <th>Before</th>
                <th>Now</th>
                <th>Rectification</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {observations.map((item, index) => {
                const statusChange = getVisualObsChange(index);
                const beforeValue = statusChange.old || item?.status_key || item?.status || '-';
                const nowValue = statusChange.new || item?.status_key || item?.status || '-';

                let rectificationStatus = item?.rectification_status || '';
                if (statusChange.newRectification) {
                  rectificationStatus = statusChange.newRectification;
                } else if (statusChange.oldRectification) {
                  rectificationStatus = statusChange.oldRectification;
                }

                return (
                  <tr key={item?.id || `obs-${index}`}>
                    <td>{index + 1}</td>
                    <td>{item?.description || '-'}</td>
                    <td className="text-danger fw-semibold">{beforeValue}</td>
                    <td className="text-success fw-semibold">{nowValue}</td>
                    <td>{renderRectificationBadge(rectificationStatus)}</td>
                    <td>{item?.remark || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  const renderAdditionalFieldsTable = (additionalFields, changes, title) => {
    if (!Array.isArray(additionalFields) || additionalFields.length === 0) return null;

    const getAdditionalFieldChange = (index) => {
      if (!changes || !changes.old || !changes.new) return {};

      const oldArray = changes.old?.additional_fields || [];
      const newArray = changes.new?.additional_fields || [];

      if (oldArray[index] || newArray[index]) {
        const oldItem = oldArray[index];
        const newItem = newArray[index];

        let oldStatusKey = '-';
        let newStatusKey = '-';
        let oldRectification = '';
        let newRectification = '';

        if (oldItem) {
          oldStatusKey = oldItem?.observation?.status_key || oldItem?.observation?.status || '-';
          oldRectification = oldItem?.observation?.rectification_status || '';
        }

        if (newItem) {
          newStatusKey = newItem?.observation?.status_key || newItem?.observation?.status || '-';
          newRectification = newItem?.observation?.rectification_status || '';
        }

        if (oldStatusKey !== newStatusKey || oldRectification !== newRectification) {
          return {
            old: oldStatusKey,
            new: newStatusKey,
            oldRectification,
            newRectification
          };
        }
      }

      return {};
    };

    return (
      <div className="mb-5">
        <h5 className="mb-3">{title}</h5>
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th width="60">#</th>
                <th>Description</th>
                <th>Before</th>
                <th>Now</th>
                <th>Rectification</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {additionalFields.map((field, index) => {
                const observation = field?.observation || {};
                const statusChange = getAdditionalFieldChange(index);
                const beforeValue = statusChange.old || observation?.status_key || observation?.status || '-';
                const nowValue = statusChange.new || observation?.status_key || observation?.status || '-';

                let rectificationStatus = observation?.rectification_status || '';
                if (statusChange.newRectification) {
                  rectificationStatus = statusChange.newRectification;
                } else if (statusChange.oldRectification) {
                  rectificationStatus = statusChange.oldRectification;
                }

                return (
                  <tr key={field?.rowId || `additional-${index}`}>
                    <td>{index + 1}</td>
                    <td>{field?.description || '-'}</td>
                    <td className="text-danger fw-semibold">{beforeValue}</td>
                    <td className="text-success fw-semibold">{nowValue}</td>
                    <td>{renderRectificationBadge(rectificationStatus)}</td>
                    <td>{observation?.remark || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render Summary
  // const renderSummary = (summary) => {
  //   if (!summary || Object.keys(summary).length === 0) return null;

  //   return (
  //     <div className="mb-5">
  //       <h5 className="mb-3">Summary</h5>
  //       <div className="d-flex gap-4 flex-wrap">
  //         <span className="badge bg-success fs-6">OK: {summary.ok || 0}</span>
  //         <span className="badge bg-danger fs-6">NOT OK: {summary.notOk || 0}</span>
  //         <span className="badge bg-warning text-dark fs-6">PENDING: {summary.pending || 0}</span>
  //         <span className="badge bg-secondary fs-6">NA: {summary.na || 0}</span>
  //       </div>
  //     </div>
  //   );
  // };

  const HistoryDetailsModal = () => {
    // if (!selectedHistory) return null;

    return (
      <Modal
        isOpen={showHistoryDetails}
        toggle={() => setShowHistoryDetails(false)}
        size="xl"
        scrollable
        centered
      >
        <ModalHeader className="position-relative">
          History Details - {getStatusLabel(selectedHistory?.flow_status || selectedHistory?.status)}
          <button
            type="button"
            onClick={() => setShowHistoryDetails(false)}
            style={{
              position: 'absolute',
              top: '12px',
              right: '15px',
              border: 'none',
              background: 'transparent',
              fontSize: '20px',
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1,
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </ModalHeader>
        <ModalBody
          style={{
            background: '#fff',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
    
          <div className="border-bottom pb-3 mb-4">
            <div className="row align-items-center">
              <div className="col-md-4">
                <h6 className="mb-0">
                  <strong>Date :</strong> {formatDate(selectedHistory?.created_at)}
                </h6>
              </div>
              <div className="col-md-4 text-md-center mt-2 mt-md-0">
                <h6 className="mb-0">
                  <strong>Status :</strong>{' '}
                  <span className={`badge ${getBadgeClass(selectedHistory?.flow_status || selectedHistory?.status)}`}>
                    {getStatusLabel(selectedHistory?.flow_status || selectedHistory?.status)}
                  </span>
                </h6>
              </div>
              <div className="col-md-4 text-md-end mt-2 mt-md-0">
                <h6 className="mb-0">
                  <strong>Shell No :</strong> {applicationDetails?.shell_no_name || shellNo || '-'}
                </h6>
              </div>
            </div>
          </div>

          
          {selectedHistory?.remark && selectedHistory.remark !== 'N/A' && (
            <div className="mb-4">
              <h6 className="fw-bold mb-2">Remark :</h6>
              <div
                className="border rounded p-3"
                style={{
                  background: '#f8f9fa',
                  minHeight: '55px',
                }}
              >
                {selectedHistory?.remark}
              </div>
            </div>
          )}

          <div className="mb-4">
            <h5 className="mb-3">Inspection Details</h5>

            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th width="60">#</th>
                    <th>Description</th>
                    <th>Before</th>
                    <th>Now</th>
                    <th>Rectification</th>
                    <th>Remarks</th>
                  </tr>
                </thead>

                <tbody>
                  {selectedHistory?.new_data?.map((item, index) => {
                    const oldItem = selectedHistory?.old_data?.[index];

                    return (
                      <tr key={item.description || `${index}-${item.status}`}>
                        <td>{index + 1}</td>

                        <td>{item?.description || 'N/A'}</td>

                        <td className="text-danger fw-semibold">
                          {oldItem?.status_key || oldItem?.status || '--'}
                        </td>

                        <td className="text-success fw-semibold">
                          {item?.status_key || item?.status || '--'}
                        </td>

                        <td>
                          {item?.rectification_status || item?.rectification || '-'}
                        </td>

                        <td>
                          {item?.remarks || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* VISUAL OBSERVATION */}
          {renderVisualObservationTable(
            selectedHistory?.visual_obs_one,
            selectedHistory?.changes,
            'Visual Observation'
          )}

          {/* ADDITIONAL FIELDS */}
          {renderAdditionalFieldsTable(
            selectedHistory?.additional_fields,
            selectedHistory?.changes,
            'Additional Fields'
          )}

         
          {selectedHistory?.length === 0 && (
              <div className="text-center py-5">
                No observation data available for this history entry
              </div>
            )}
        </ModalBody>
      </Modal>
    );
  };

  const closeButton = (
    <button
      type="button"
      className="close"
      onClick={toggle}
      style={{
        position: 'absolute',
        right: '20px',
        top: '20px',
        background: 'none',
        border: 'none',
        fontSize: '1.5rem',
        cursor: 'pointer',
        color: '#000',
        opacity: '0.7',
      }}
    >
      <span aria-hidden="true">&times;</span>
    </button>
  );

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
        <ModalHeader toggle={toggle} close={closeButton}>
          <h5 className="mb-0">Flow History</h5>
          {/* <h5 className="mb-0">Flow History -{applicationDetails?.shell_no_name || shellNo ? `(Shell No: ${applicationDetails?.shell_no_name || shellNo})` : ''}</h5> */}
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <div>
              {history.length === 0 && !applicationDetails ? (
                <ul className="verti-timeline list-unstyled">
                  <li className="event-list">
                    <h6>No history records found</h6>
                  </li>
                </ul>
              ) : (
                <ul className="verti-timeline list-unstyled">                 
                  {history.map((flow) => (
                    <li className="event-list" key={flow?.id || flow?._id}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="event-date text-primary">
                            {formatDate(flow?.created_at)}
                          </div>
                          <h6>
                            Status Changed to{' '}
                            <span className={`badge ${getBadgeClass(flow?.flow_status || flow?.status)}`}>
                              {getStatusLabel(flow?.flow_status || flow?.status)}
                            </span>{' '}
                            by <strong>{flow?.inspector?.name || 'Unknown User'}</strong> 
                            {flow?.action === 'create' && <span className="badge bg-warning text-dark">New Application</span>}
                            {flow?.flow_status === 2 && <span> at <span className="badge bg-info">{flow?.created_at? new Date(flow.created_at).toLocaleDateString('en-GB').replace(/\//g, '-'): '-'}</span></span>}
                          </h6>
                          {flow?.remark && (
                            <small className="text-muted d-block mt-1">
                              <i className="bi bi-chat"></i> {flow?.remark}
                            </small>
                          )}
                        </div>
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => handleViewHistoryDetails(flow)}
                        >
                          <i className="bi bi-eye" /> View
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>

      <HistoryDetailsModal />
    </>
  );
};

LuggageRackFlowHistoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  stageId: PropTypes.string,
  shellNo: PropTypes.string,
};

LuggageRackFlowHistoryModal.defaultProps = {
  stageId: null,
  shellNo: '',
};

export default LuggageRackFlowHistoryModal;